import React from 'react';
import { useTranslation } from 'react-i18next';
import { RESOURCE_CATEGORIES } from '../../../types/resources';

interface Props {
  values: {
    name: string;
    quantity: string;
    unit: string;
    category: string;
    minimum_quantity?: string;
    location?: string;
    expiry_date?: string;
    notes?: string;
  };
  errors: Record<string, string>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  disabled?: boolean;
}

const ResourceFormFields: React.FC<Props> = ({ values, errors, onChange, disabled = false }) => {
  const { t } = useTranslation();

  // Format date for input value, preserving the local date
  const formatDateForInput = (date?: string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-CA'); // Use ISO format YYYY-MM-DD
  };

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t('resources.resourceName')} *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={values.name}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
          {t('common.category')} *
        </label>
        <select
          id="category"
          name="category"
          value={values.category}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        >
          <option value="">{t('common.select')}</option>
          {RESOURCE_CATEGORIES.map(category => (
            <option key={category.value} value={category.value}>
              {t(`resources.category.${category.value}`)}
            </option>
          ))}
        </select>
        {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
            {t('common.quantity')} *
          </label>
          <input
            type="number"
            id="quantity"
            name="quantity"
            value={values.quantity}
            onChange={onChange}
            disabled={disabled}
            min="0"
            step="0.01"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.quantity && <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>}
        </div>

        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700">
            {t('common.unit')} *
          </label>
          <input
            type="text"
            id="unit"
            name="unit"
            value={values.unit}
            onChange={onChange}
            disabled={disabled}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.unit && <p className="mt-1 text-sm text-red-600">{errors.unit}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="minimum_quantity" className="block text-sm font-medium text-gray-700">
          {t('resources.minimumQuantity')}
        </label>
        <input
          type="number"
          id="minimum_quantity"
          name="minimum_quantity"
          value={values.minimum_quantity}
          onChange={onChange}
          disabled={disabled}
          min="0"
          step="0.01"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        />
        {errors.minimum_quantity && (
          <p className="mt-1 text-sm text-red-600">{errors.minimum_quantity}</p>
        )}
      </div>

      <div>
        <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700">
          {t('resources.expiryDate')}
        </label>
        <input
          type="date"
          id="expiry_date"
          name="expiry_date"
          value={formatDateForInput(values.expiry_date)}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        />
        {errors.expiry_date && (
          <p className="mt-1 text-sm text-red-600">{errors.expiry_date}</p>
        )}
      </div>

      <div>
        <label htmlFor="location" className="block text-sm font-medium text-gray-700">
          {t('common.location')}
        </label>
        <input
          type="text"
          id="location"
          name="location"
          value={values.location}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        />
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          {t('common.notes')}
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={3}
          value={values.notes}
          onChange={onChange}
          disabled={disabled}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
        />
      </div>
    </div>
  );
};

export default ResourceFormFields;